import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Alert, Button, Modal, FormStack } from "@marekl/adminer/components";
import { notifyError } from "@marekl/adminer/api";
import { ProvisioningCode } from "../ProvisioningCode";
import styles from "./TokenRevealModal.module.css";

interface TokenRevealModalProps {
  open: boolean;
  title: string;
  /** e.g. "API key" / "token" / "signing secret" — used in the copy. */
  noun: string;
  token: string;
  /**
   * When set, the modal also shows a code a worker phone can scan instead of
   * having the token typed into it. Only worker tokens have anything to
   * provision, so only they pass it.
   */
  provision?: { name: string };
  onClose: () => void;
}

/**
 * One-time secret reveal after creating an API key / worker token / webhook.
 * The value is never retrievable again, so the modal pushes copy-to-clipboard
 * hard and warns before closing.
 *
 * A worker token also gets a code to scan. That is the only moment it can be
 * offered — the token is in the clear here and nowhere else afterwards — and
 * it is the difference between provisioning a phone in a second and typing
 * forty random characters on a touchscreen.
 */
export function TokenRevealModal({
  open,
  title,
  noun,
  token,
  provision,
  onClose,
}: TokenRevealModalProps) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      notifyError(err);
    }
  }

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={<Button variant="primary" onClick={onClose}>Got it, close</Button>}
    >
      <FormStack>
        <Alert variant="warn" title="Shown only once">
          Store the {noun} now — once this window is closed it cannot be
          displayed again.
        </Alert>
        {provision ? (
          <ProvisioningCode token={token} name={provision.name} />
        ) : null}
        <div className={styles.tokenRow}>
          <code className={styles.token}>{token}</code>
          <Button
            variant="primary" weight="outline"
            size="sm"
            onClick={copy}
            startIcon={
              copied ? (
                <Check size={14} aria-hidden="true" />
              ) : (
                <Copy size={14} aria-hidden="true" />
              )
            }
          >
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </FormStack>
    </Modal>
  );
}
